import { PackageOpen } from "lucide-react";

import type { LineEligibility, LookupResponse } from "@sirdab/returns-shared";
import { Alert, EmptyState } from "@sirdab/ui";

import { LineItemRow, QtyStepper, StepActions } from "../components";
import { useFlow, type FlowLineDraft } from "../flow";
import { useMessages } from "../i18n";
import { RequireLookup } from "./shared";
import { StepScaffold } from "./StepScaffold";

/**
 * Step 2 of docs/08 — every line on the order.
 *
 * ADR-007 governs this screen: an ineligible line stays VISIBLE and greyed, carrying the
 * server's own `reasonText`. Filtering it out is what produces the "why can't I see my
 * item" support ticket this product exists to remove, so there is deliberately no code
 * path here that drops a line from the list.
 *
 * Selection is a client-side convenience. The server re-validates every line on submit.
 */
function Body({ lookup }: { lookup: LookupResponse }): React.JSX.Element {
  const { t } = useMessages();
  const { lines: drafts, update, next, goTo } = useFlow();

  const eligibilityOf = (lineRef: string): LineEligibility | undefined =>
    lookup.lines.find((entry) => entry.lineRef === lineRef);

  const draftOf = (lineRef: string): FlowLineDraft | undefined =>
    drafts.find((draft) => draft.lineRef === lineRef);

  function toggle(lineRef: string): void {
    const existing = draftOf(lineRef);
    if (existing !== undefined) {
      update({ lines: drafts.filter((draft) => draft.lineRef !== lineRef) });
      return;
    }
    update({
      lines: [
        ...drafts,
        {
          lineRef,
          qty: 1,
          reasonCode: null,
          subReason: null,
          note: null,
          uploadIds: [],
        },
      ],
    });
  }

  function setQty(lineRef: string, qty: number): void {
    update({
      lines: drafts.map((draft) =>
        draft.lineRef === lineRef ? { ...draft, qty } : draft,
      ),
    });
  }

  const orderLines = lookup.order.lines;
  const anyEligible = orderLines.some(
    (line) => eligibilityOf(line.lineRef)?.eligible === true,
  );

  if (orderLines.length === 0) {
    return (
      <EmptyState
        icon={PackageOpen}
        title={t("items.empty")}
        className="py-8"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body text-muted-foreground">
        {t("items.select_hint")}
      </p>

      {/* Stated once, above a list that still shows every line and its reason. */}
      {!anyEligible ? (
        <Alert variant="warning">{t("items.none_eligible")}</Alert>
      ) : null}

      <ul className="flex flex-col">
        {orderLines.map((line) => {
          const eligibility = eligibilityOf(line.lineRef);
          const draft = draftOf(line.lineRef);
          return (
            <LineItemRow
              key={line.lineRef}
              line={line}
              {...(eligibility === undefined ? {} : { eligibility })}
              selected={draft !== undefined}
              onToggle={toggle}
            >
              {draft !== undefined && line.qty > 1 ? (
                <QtyStepper
                  value={draft.qty}
                  max={line.qty}
                  onChange={(qty) => setQty(line.lineRef, qty)}
                  label={t("field.quantity.label")}
                />
              ) : null}
            </LineItemRow>
          );
        })}
      </ul>

      <StepActions
        onNext={next}
        onBack={() => goTo("lookup")}
        nextDisabled={drafts.length === 0}
      />
    </div>
  );
}

export function ItemSelectionScreen(): React.JSX.Element {
  return (
    <StepScaffold
      step="items"
      titleKey="screen.items.title"
      descriptionKey="screen.items.description"
    >
      <RequireLookup>{(lookup) => <Body lookup={lookup} />}</RequireLookup>
    </StepScaffold>
  );
}
